import type { PorchColumnPattern } from "building-types/columns/porchColumnPattern";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { FoundationAssemblyOverrides } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationPattern } from "building-types/foundations/foundationPatternTypes";
import type { FormulaDisabled } from "building-types/options/formulaDisabled";
import type { WallAssemblyOperator } from "building-types/walls/wallAssemblyOverrideTypes";
import type { LineAnchor } from "geom/polyLine2Types";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import { EdgeBehaviorType } from "./edgeBehaviorTypesEnum";

export type EdgeBehavior =
  | DimensionEdgeBehavior
  | ElevationNoteEdgeBehavior
  | FoundationAssemblyEdgeBehavior
  | FoundationPatternEdgeBehavior
  | LabelEdgeBehavior
  | PorchColumnEdgeBehavior
  | TrimEdgeBehavior
  | WallAssemblyEdgeBehavior;

export type EdgeBehaviorTypeToEdgeBehavior = {
  [EdgeBehaviorType.DIMENSION]: DimensionEdgeBehavior;
  [EdgeBehaviorType.ELEVATION_NOTE]: ElevationNoteEdgeBehavior;
  [EdgeBehaviorType.FOUNDATION_ASSEMBLY]: FoundationAssemblyEdgeBehavior;
  [EdgeBehaviorType.FOUNDATION_PATTERN]: FoundationPatternEdgeBehavior;
  [EdgeBehaviorType.LABEL]: LabelEdgeBehavior;
  [EdgeBehaviorType.PORCH_COLUMN]: PorchColumnEdgeBehavior;
  [EdgeBehaviorType.TRIM]: TrimEdgeBehavior;
  [EdgeBehaviorType.WALL_ASSEMBLY]: WallAssemblyEdgeBehavior;
};

interface BaseEdgeBehavior extends FormulaDisabledDocEntityBase<"EDGE_BEHAVIOR"> {
  readonly id: PrimaryEntityId<"EDGE_BEHAVIOR">;
  readonly parentId: DocEntityParentId<"ROOM" | "STAIR_BLOCK">;
  readonly edgeId: string;
}

export interface ElevationNoteEdgeBehavior extends EdgeElevationNote, BaseEdgeBehavior {
  readonly edgeBehaviorType: EdgeBehaviorType.ELEVATION_NOTE;
}

export interface EdgeElevationNote extends FormulaDisabled {
  readonly edgeId: string;
  readonly offset: number;
  readonly param: number;
  readonly paramNormalized: boolean;
  readonly paramAnchor: LineAnchor;
  readonly datumId?: ForeignEntityId<"DATUM">;
  readonly text: string;
  readonly rotation: number; // in degrees
  readonly mirrorWithBuilding: boolean;
}

export interface WallAssemblyEdgeBehavior extends BaseEdgeBehavior {
  readonly edgeBehaviorType: EdgeBehaviorType.WALL_ASSEMBLY;
  readonly wallAssemblyOperator: WallAssemblyOperator;
  readonly outside?: boolean;
}

export interface DimensionEdgeBehavior extends BaseEdgeBehavior {
  readonly edgeBehaviorType: EdgeBehaviorType.DIMENSION;
  readonly disableInteriorAuto?: boolean;
  readonly disableExteriorAuto?: boolean;
  readonly disableDiagonalAuto?: boolean;
}

export interface TrimEdgeBehavior extends BaseEdgeBehavior {
  readonly edgeBehaviorType: EdgeBehaviorType.TRIM;
  readonly hasBaseTrimMolding?: boolean;
  readonly hasCrownTrimMolding?: boolean;
}

export interface PorchColumnEdgeBehavior extends BaseEdgeBehavior {
  readonly edgeBehaviorType: EdgeBehaviorType.PORCH_COLUMN;
  readonly porchColumnPattern: PorchColumnPattern;
}

export interface FoundationAssemblyEdgeBehavior extends BaseEdgeBehavior {
  readonly edgeBehaviorType: EdgeBehaviorType.FOUNDATION_ASSEMBLY;
  readonly foundationAssemblyOverrides: FoundationAssemblyOverrides;
}

export interface FoundationPatternEdgeBehavior extends BaseEdgeBehavior {
  readonly edgeBehaviorType: EdgeBehaviorType.FOUNDATION_PATTERN;
  readonly foundationPattern: FoundationPattern;
}

export interface LabelEdgeBehavior extends BaseEdgeBehavior {
  readonly edgeBehaviorType: EdgeBehaviorType.LABEL;
  readonly facadeId: ForeignEntityId<"FACADE">;
}

export function isIsolatedFoundationPatternEdgeBehavior(
  edgeBehavior: EdgeBehavior,
): edgeBehavior is FoundationPatternEdgeBehavior {
  return (
    edgeBehavior.edgeBehaviorType === EdgeBehaviorType.FOUNDATION_PATTERN &&
    edgeBehavior.foundationPattern?.patternType === "ISOLATED"
  );
}
